package com.eladfinish.bible.contest.screens.randomVerse.usecases

import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.analytics.crashlytics.AppCrashlytics
import com.eladfinish.bible.contest.at_least_one_book
import com.eladfinish.bible.contest.common.ChaptersRepository
import com.eladfinish.bible.contest.common.ChaptersRepository.Companion.migrateFilename
import com.eladfinish.bible.contest.common.usecases.PartitionChapterUseCase
import com.eladfinish.bible.contest.common.usecases.RemoveTeamimUseCase
import com.eladfinish.bible.contest.common.usecases.VersePickTarget
import com.eladfinish.bible.contest.constants.BooleanConstants
import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.constants.NUMBER_OF_CHAPTERS
import com.eladfinish.bible.contest.constants.SharedPrefsConstants
import com.eladfinish.bible.contest.constants.SharedPrefsConstants.KEY_ENABLE_HALF_VERSE
import com.eladfinish.bible.contest.constants.StringConstants
import com.eladfinish.bible.contest.constants.StringConstants.ALL_CHAPTERS
import com.eladfinish.bible.contest.constants.StringConstants.COLON_SPLITTER
import com.eladfinish.bible.contest.constants.strings.CommonVerses
import com.eladfinish.bible.contest.data.common.AssetReader
import com.eladfinish.bible.contest.data.common.stringResolver.StringResolver
import com.eladfinish.bible.contest.domain.catalog.BibleCatalog.books
import com.eladfinish.bible.contest.extensions.makeContinuousUnderlinedKMP
import com.eladfinish.bible.contest.extensions.replaceRegex
import com.eladfinish.bible.contest.screens.mikud.MikudRepository
import com.eladfinish.bible.contest.screens.mikud.usecases.GetRandomBookRandomChapterFilenameUseCase
import com.eladfinish.bible.contest.screens.mikud.usecases.PickRandomChapterUseCase
import com.eladfinish.bible.contest.screens.openQuestions.model.QuestionItemJson
import com.eladfinish.bible.contest.screens.openQuestions.model.QuestionItemJsonVisibility
import com.eladfinish.bible.contest.screens.randomVerse.RandomVerseStore.Companion.MULTIPLE_RANDOM_VERSES_SIZE
import com.eladfinish.bible.contest.screens.randomVerse.model.RandomizedVerse
import com.eladfinish.bible.contest.screens.search.model.VerseUiModel
import com.eladfinish.bible.contest.screens.search.usecases.PickVerseFromChapterUseCaseV2
import com.eladfinish.bible.contest.utils.GetParashotUseCase
import com.eladfinish.bible.contest.utils.MyStringUtils
import com.eladfinish.bible.contest.utils.addGershaimAndColon
import com.eladfinish.bible.contest.utils.replaceKtivWithQriNew
import com.eladfinish.bible.contest.utils.setNoBreakSpaceBeforePasek
import com.eladfinish.bible.contest.utils.toOtiot
import com.russhwolf.settings.Settings
import dev.icerock.moko.resources.desc.desc
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import kotlin.random.Random

/**
 * Generates a batch of random verses as [QuestionItemJsonVisibility] items —
 * the same logic previously living in RandomVerseStore.generateMultipleRandomVerses.
 *
 * Extracted to a standalone use case so that it can be injected into any Store
 * without creating a ViewModel-to-ViewModel dependency.
 */
class GenerateMultipleRandomVersesUseCase(
    private val settings: Settings,
    private val stringResolver: StringResolver,
    private val assetReader: AssetReader,
    private val mikudRepository: MikudRepository,
    private val getRandomBookRandomChapterFilenameUseCase: GetRandomBookRandomChapterFilenameUseCase,
    private val pickRandomChapterUseCase: PickRandomChapterUseCase,
    private val pickVerseFromChapterUseCaseV2: PickVerseFromChapterUseCaseV2,
    private val getParashotUseCase: GetParashotUseCase,
    private val partitionChapterUseCase: PartitionChapterUseCase,
    private val getNamesToHideUseCase: GetNamesToHideUseCase,
    private val hidePeopleNamesUsecase: HidePeopleNamesUsecase,
    private val removeTeamimUseCase: RemoveTeamimUseCase,
) {

    private val numOfChaptersInMikud: Int
        get() = settings.getInt(Constants.PREF_NUM_OF_CHAPTERS_IN_MIKUD, NUMBER_OF_CHAPTERS)

    private val randomApproach: String
        get() = settings.getString(key = Constants.PREF_RANDOM_APPROACH, defaultValue = Constants.APPROACH_BY_BOOK)

    suspend operator fun invoke(onEmptyMikud: () -> Unit): List<QuestionItemJsonVisibility> {
        if (numOfChaptersInMikud == 0) {
            withContext(Dispatchers.Main) { onEmptyMikud() }
            return emptyList()
        }
        return withContext(Dispatchers.Default) {
            List(MULTIPLE_RANDOM_VERSES_SIZE) {
                val randomizedVerse = getRandomVerse(onEmptyMikud = onEmptyMikud)
                val item = randomizedVerse.verseUiModel
                val rawContent = randomizedVerse.rawContent
                val spannableStringKMP = if (settings.getBoolean(
                        key = KEY_ENABLE_HALF_VERSE,
                        defaultValue = BooleanConstants.ENABLE_HALF_VERSE_DEFAULT,
                    )
                ) {
                    randomizedVerse.halfSpannableStringKMP
                } else {
                    randomizedVerse.fullSpannableStringKMP
                }
                val itemJson = QuestionItemJson(
                    question = spannableStringKMP.charSequence.toString(),
                    answer = "$rawContent$COLON_SPLITTER ${item.source}",
                    book = bookName(item.bookIndex),
                    chapter = item.chapterIndex.toOtiot(),
                    verse = item.verse,
                    author = "",
                )
                QuestionItemJsonVisibility(questionItemJson = itemJson, answerIsVisible = false)
            }
        }
    }

    private fun getRandomVerse(onEmptyMikud: () -> Unit): RandomizedVerse {
        if (numOfChaptersInMikud == 0) {
            onEmptyMikud()
            return RandomizedVerse(
                verseUiModel = errorVerseUiModel(
                    stringResolver.resolveString(SharedRes.strings.at_least_one_book.desc())
                )
            )
        }

        val chapterFilename: String
        val bookIndex: Int
        try {
            val mikudStr = mikudRepository.mikudList
            if (mikudStr.isEmpty()) {
                return RandomizedVerse(
                    verseUiModel = errorVerseUiModel(
                        stringResolver.resolveString(SharedRes.strings.at_least_one_book.desc())
                    )
                )
            }
            chapterFilename = if (randomApproach == Constants.APPROACH_BY_BOOK) {
                getRandomBookRandomChapterFilenameUseCase(mikudStr = mikudStr)
            } else {
                pickRandomChapterUseCase(chapterList = mikudStr)
            }.migrateFilename()

            val chapterContent = assetReader.readFile(
                fileName = ALL_CHAPTERS + assetReader.fileSeparator + chapterFilename
            )
            bookIndex = ChaptersRepository.getBookNumber(chapterFilename)

            var verseUiModel: VerseUiModel?
            var hasIdenticalVerse: Boolean
            do {
                hasIdenticalVerse = false
                verseUiModel = pickVerseFromChapterUseCaseV2(
                    bookIndex = bookIndex,
                    chapterContent = chapterContent,
                    target = VersePickTarget.Random,
                )!!
                val rawContent = verseUiModel.rawContent()
                val rawNoTeamim = rawContent
                    .replaceRegex(StringConstants.Regex.TEAMIM_CHARS, "")
                    .replaceRegex("\\s+", " ")
                if (listOf(
                        CommonVerses.VAYDABER_LEMOR,
                        CommonVerses.VAYHI_DEVAR_EL,
                        CommonVerses.VAYOMER_HASHEM,
                    ).map { it.toRegex() }.any { rawNoTeamim.matches(it) }
                    || rawContent == CommonVerses.VAYHI_DEVAR_ELAY
                ) {
                    hasIdenticalVerse = true
                }
            } while (hasIdenticalVerse)

            val rawContent = verseUiModel.rawContent()
            val parasha = getParashotUseCase.getParashaByBookChapterAndVerse(
                book = bookName(bookIndex),
                chapter = verseUiModel.chapterIndex.toOtiot(),
                verse = verseUiModel.verse,
            )
            val namesToHide = StringBuilder()
            val fullSpannable = stylizeVerse(rawContent, chapterFilename, parasha, namesToHide)
            val halfSpannable = stylizeVerse(
                getPartitionedVerse(verseUiModel, rawContent),
                chapterFilename,
                parasha,
                namesToHide,
            )

            return RandomizedVerse(
                verseUiModel = verseUiModel,
                rawContent = rawContent,
                parasha = parasha,
                halfSpannableStringKMP = halfSpannable,
                fullSpannableStringKMP = fullSpannable,
            )
        } catch (e: Exception) {
            AppCrashlytics.logException(e)
            return RandomizedVerse(verseUiModel = errorVerseUiModel("error: ${e.message}"))
        }
    }

    private fun stylizeVerse(
        content: String,
        chapterFilename: String,
        parasha: String,
        namesToHide: StringBuilder,
    ) = content
        .showOnlyQriIfNeeded()
        .hideNamesIfNeeded(chapterFilename, parasha, namesToHide)
        .hideNikudIfNeeded()
        .hideTeamimIfNeeded().setNoBreakSpaceBeforePasek().addGershaimAndColon()
        .makeContinuousUnderlinedKMP()

    private fun String.showOnlyQriIfNeeded(): String =
        if (settings.getBoolean(SharedPrefsConstants.ONLY_QRI_SWITCH, true))
            replaceKtivWithQriNew()
        else this

    private fun String.hideNamesIfNeeded(
        chapterFilename: String,
        parasha: String,
        namesToHide: StringBuilder,
    ): String {
        if (!settings.getBoolean(Constants.PREF_HIDE_PEOPLE_NAMES_SWITCH, true)) return this
        namesToHide.append("שמות להסרה:")
        val namesToHideList = getNamesToHideUseCase(
            chapterFilename = chapterFilename,
            parasha = parasha,
            namesToHide = namesToHide,
        )
        return hidePeopleNamesUsecase.getVerseWithHiddenNames(
            names = namesToHideList,
            randomVerseStr = this,
        ).verseWithBlanks
    }

    private fun String.hideNikudIfNeeded(): String =
        if (!settings.getBoolean(Constants.PREF_NIKUD_SWITCH, true))
            replaceRegex(StringConstants.Regex.NIKUD_CHARS, "")
        else this

    private fun String.hideTeamimIfNeeded(): String =
        if (!settings.getBoolean(
                Constants.PREF_SHOW_WITH_TEAMIM,
                BooleanConstants.ENABLE_SHOW_WITH_TEAMIM_PREF
            )
        ) removeTeamimUseCase(pasuk = this)
        else this

    private fun getPartitionedVerse(verseUiModel: VerseUiModel, rawContent: String): String {
        val verse = "${verseUiModel.verse} $rawContent$COLON_SPLITTER"
        val book = bookName(verseUiModel.bookIndex)
        val fromSifreyEmet = MyStringUtils.isFromSifreyEmet(book)
        val partitioned = partitionChapterUseCase(chapter = verse, isFromSifreyEmet = fromSifreyEmet)
        val firstArray = partitioned[0]
        val partsArray = firstArray.subList(fromIndex = 1, toIndex = firstArray.size)
        return try {
            val size = partsArray.size
            val num = Random.nextInt(partsArray.size)
            val part = MyStringUtils.retrieveVerse(partsArray[num])
            val ellipsis = "..."
            when {
                size == 3 && num == 1 ->
                    if (partsArray[1].size == 1) "$ellipsis $part ${MyStringUtils.retrieveVerse(partsArray[2])}"
                    else "$part $ellipsis"

                num == 0 -> "$part $ellipsis"
                else -> "$ellipsis $part"
            }
        } catch (e: Exception) {
            AppCrashlytics.logException(e)
            "---"
        }
    }

    private fun bookName(bookIndex1Based: Int): String =
        books.getOrNull(bookIndex1Based - 1)?.hebrew ?: ""

    private fun VerseUiModel.rawContent(): String =
        text.removePrefix("\"").let { stripped ->
            val colonIdx = stripped.lastIndexOf(COLON_SPLITTER)
            if (colonIdx >= 0) stripped.removeRange(colonIdx, stripped.length).trimEnd('"')
            else stripped.trimEnd('"')
        }

    private fun errorVerseUiModel(message: String) = VerseUiModel(
        id = "error",
        text = message,
        source = "",
        highlightedRanges = kotlinx.collections.immutable.persistentListOf(),
        bookIndex = 0,
        chapterIndex = 0,
        verse = "",
    )
}
